import { useEffect, useRef, useState } from 'react';

interface FullscreenDivReturn {
  enterFullscreen: () => void;
  exitFullscreen: () => void;
  isFullscreen: boolean;
  divRef: React.RefObject<HTMLDivElement>;
}

const useFullscreenDiv = (): FullscreenDivReturn => {
  const divRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(
    !!document.fullscreenElement
  );

  const enterFullscreen = () => {
    const element = divRef.current;

    if (element) {
      if ('requestFullscreen' in element) {
        element.requestFullscreen();
      } else if ('mozRequestFullScreen' in element) {
        (
          element as {
            mozRequestFullScreen: () => void;
          }
        ).mozRequestFullScreen();
      } else if ('webkitRequestFullscreen' in element) {
        (
          element as {
            webkitRequestFullscreen: () => void;
          }
        ).webkitRequestFullscreen();
      } else if ('msRequestFullscreen' in element) {
        (
          element as {
            msRequestFullscreen: () => void;
          }
        ).msRequestFullscreen();
      }
    }
  };

  const exitFullscreen = () => {
    if ('exitFullscreen' in document) {
      document.exitFullscreen();
    } else if ('mozCancelFullScreen' in document) {
      (
        document as {
          mozCancelFullScreen: () => void;
        }
      ).mozCancelFullScreen();
    } else if ('webkitExitFullscreen' in document) {
      (
        document as {
          webkitExitFullscreen: () => void;
        }
      ).webkitExitFullscreen();
    } else if ('msExitFullscreen' in document) {
      (
        document as {
          msExitFullscreen: () => void;
        }
      ).msExitFullscreen();
    }
  };

  const handleFullscreenChange = () => {
    setIsFullscreen(!!document.fullscreenElement);
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'MSFullscreenChange',
        handleFullscreenChange
      );
    };
  }, []);

  return { enterFullscreen, exitFullscreen, isFullscreen, divRef };
};

export default useFullscreenDiv;
